import { OrderStatus, StructureStatus } from "@enums";
import { Box, Chip, TableCell, Typography } from "@mui/material";
import { OrderSearchOut } from "@providers/hop-ord-server/api";
import AutomationInProgress from "@pages/CommandCenter/CommandCenterTable/AutomationInProgress";

interface Props {
  order: OrderSearchOut;
  index: number;
}

export const ChipLabels = {
  DRAFT: "Draft",
  NOT_READY_FOR_PLANNING: "Not ready for planning",
  READY_FOR_PLANNING: "Ready for planning",
  PENDING_PLAN_REVIEW: "Pending plan review",
  PLAN_APPROVED: "Approved",
  PLAN_REJECTED: "Rejected",
  HANDOVER_COMPLETE: "Handover complete",
};

const PlanningStatusCell = ({ order, index }: Props) => {
  const notReadyForPlanning =
    order.status === OrderStatus.SUBMITTED &&
    (order.rtstructStatus !== StructureStatus.COMPLETE ||
      order.ctStatus !== StructureStatus.COMPLETE);

  const readyForPlanning =
    order.status === OrderStatus.SUBMITTED &&
    order.rtstructStatus === StructureStatus.COMPLETE &&
    order.ctStatus === StructureStatus.COMPLETE;

  let content = null;

  if (order.status === OrderStatus.DRAFT) {
    content = (
      <Chip label={ChipLabels.DRAFT} sx={{ backgroundColor: "grey.200" }} />
    );
  }

  if (readyForPlanning) {
    content = (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ flex: 2 }}>
          <Chip
            label={ChipLabels.READY_FOR_PLANNING}
            sx={{ backgroundColor: "info.light" }}
          />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
          }}
        >
          CT & RTstruct available
        </Typography>
      </Box>
    );
  }

  if (notReadyForPlanning) {
    content = (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ flex: 2 }}>
          <Chip label={ChipLabels.NOT_READY_FOR_PLANNING} />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
          }}
        >
          Missing CT & RTstruct
        </Typography>
      </Box>
    );
  }

  if (order.status === OrderStatus.SENT_TO_AUTOMATION) {
    content = <AutomationInProgress order={order} />;
  }

  if (order.status === OrderStatus.READY_FOR_RO_APPROVAL) {
    content = (
      <Chip
        label={ChipLabels.PENDING_PLAN_REVIEW}
        sx={{ backgroundColor: "warning.main" }}
      />
    );
  }

  if (order.status === OrderStatus.APPROVED) {
    content = (
      <Chip
        label={ChipLabels.PLAN_APPROVED}
        sx={{ backgroundColor: "success.main" }}
      />
    );
  }

  if (order.status === OrderStatus.REJECTED) {
    content = (
      <Chip
        label={ChipLabels.PLAN_REJECTED}
        sx={{ backgroundColor: "error.main" }}
      />
    );
  }

  if (order.status === OrderStatus.COMPLETED) {
    content = (
      <Chip
        label={ChipLabels.HANDOVER_COMPLETE}
        sx={{ backgroundColor: "success.main" }}
      />
    );
  }

  return (
    <TableCell
      data-testid={`${index}-planning-status`}
      sx={{ minWidth: "250px" }}
    >
      {content ?? "-"}
    </TableCell>
  );
};

export default PlanningStatusCell;
